
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Spinner, Alert, Tooltip } from '@dopomogai/ui';
import { useAgentTools } from '@dopomogai/agent-kit';
import { Sparkles, Lock } from 'lucide-react';
import { toast } from '@dopomogai/ui';
import { ToolList } from '../platform_features/agent-builder/components/lists/ToolList';
import type { CustomToolData, PlatformToolData } from '@dopomogai/types';

interface AbilitiesCardProps {
  agentId: string;
  readOnly?: boolean;
}

export const AbilitiesCard: React.FC<AbilitiesCardProps> = ({ agentId, readOnly = false }) => {
  const navigate = useNavigate();
  const { agentTools, availableTools, updateTools, isLoading, error } = useAgentTools(agentId);

  const handleToggleTool = async (toolId: string) => {
    if (readOnly) {
      toast.info('You have view-only access.');
      return;
    }
    const toolToToggle = availableTools?.find(t => t.id === toolId);
    if (!toolToToggle) return;

    // Agent-level toggling: update set of linked tool slugs
    const currentSlugs = new Set(agentTools?.map(t => t.slug) || []);

    if (currentSlugs.has(toolToToggle.slug)) {
      currentSlugs.delete(toolToToggle.slug);
    } else {
      currentSlugs.add(toolToToggle.slug);
    }

    try {
      await updateTools(Array.from(currentSlugs));
      toast.success('Abilities updated!');
    } catch (e: any) {
      toast.error(`Failed to update abilities: ${e.message}`);
    }
  };

  const { platformToolsUi, customToolsUi, hasCustomTools } = useMemo(() => {
    const atSet = new Set((agentTools || []).map(t => t.slug));
    const all = (availableTools ?? []).map(t => ({
      id: t.id,
      slug: t.slug,
      name: t.name,
      description: t.description ?? undefined,
      isEnabled: atSet.has(t.slug),
      isPlatform: !!t.is_platform_tool,
    }));

    all.sort((a, b) => {
      const byEnabled = Number(b.isEnabled) - Number(a.isEnabled);
      if (byEnabled !== 0) return byEnabled;
      return a.name.localeCompare(b.name);
    });

    const platformToolsUi: PlatformToolData[] = all
      .filter(t => t.isPlatform)
      .map((t) => ({
        id: t.id,
        nameKey: t.name,
        descriptionKey: t.description || '',
        isEnabled: t.isEnabled,
        isMutable: !readOnly, // disable toggles in read-only mode
        icon: 'cpu',
      }));

    const customToolsUi: CustomToolData[] = all
      .filter(t => !t.isPlatform)
      .map((t) => ({
        id: t.id,
        name: t.name,
        description: t.description,
        isEnabled: t.isEnabled,
        isMutable: !readOnly,
        icon: 'construction',
      }));

    const hasCustomTools = (availableTools ?? []).some(x => !x.is_platform_tool);

    return { platformToolsUi, customToolsUi, hasCustomTools };
  }, [availableTools, agentTools, readOnly]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center h-40">
          <Spinner text="Loading abilities..." />
        </div>
      );
    }
    if (error) return <Alert type="error" title="Error" message={error.message} />;

    return (
      <div className="space-y-6">
        {platformToolsUi.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-slate-800">Platform tools</h4>
            <ToolList
              tools={platformToolsUi}
              toolType="platform"
              onToggleTool={handleToggleTool}
            />
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-800">Custom tools</h4>
            <div className="flex items-center gap-2">
              {readOnly && (
                <Tooltip text="View-only">
                  <Lock size={16} className="text-slate-500" />
                </Tooltip>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/app/tools')}
                className="border-2 border-black hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-[4px_4px_0_#000]"
              >
                Open Tools Hub
              </Button>
            </div>
          </div>
          <ToolList
            tools={customToolsUi}
            toolType="custom"
            onToggleTool={handleToggleTool}
            onEditTool={id => navigate(`/app/tools/${id}/edit`)}
            onDeleteTool={() =>
              toast.info('Tool deletion is managed from the Tools Hub.')
            }
          />
        </div>
      </div>
    );
  };

  return (
    <Card
      title="Abilities"
      icon={<Sparkles size={18} />}
      headerBgColor="bg-amber-100"
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Enable or disable tools this agent can use to perform actions.
        </p>
        {!hasCustomTools && (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">
              To unlock additional abilities, hop on a 15-minute concierge prescreening call with the team.
            </p>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-2 border-black hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-[4px_4px_0_#000]"
            >
              <a href="https://www.dopomogai.com/contact" target="_blank" rel="noopener noreferrer">
                Book the pre-screening call
              </a>
            </Button>
          </div>
        )}

        {renderContent()}
      </div>
    </Card>
  );
};

export default AbilitiesCard;